/**
 * "Roles or access needed" as a Bluestar area picker (8 Oct, product owner).
 *
 * Bluestar is a module inside 365FO ("365FO_BS" on the forms) managed by the
 * engineers, not the business owners. With "Roles or access needed" free
 * text, people typed "MO", "MOUSA", "CEI"… So when the form's "System or
 * application" is 365FO_BS (or Bluestar) the field becomes a pick-list of the
 * Bluestar areas of the Master approval matrix — each with View or Design —
 * its global roles / add-ons, and "Other (type it)".
 *
 * What reaches Ivanti stays text, so Ivanti and its workflows are unaffected:
 * "Bluestar – MO – View; Bluestar – CEI – Design; Bluestar – Change LOB
 * (global role / add-on); Bluestar – Other: <their words>". The text is the
 * only state: the picker reads its ticks back out of it (`parseAreasText`),
 * so the summary step, Back and a prefilled answer all keep working, and the
 * server reads the same text back to name the matrix's approvers.
 *
 * Pure (no React, no aliases), so the node tests import it directly.
 */

/** The parts of a form parameter these rules read. */
export interface AreasParameter {
  parameter_id: string;
  internal_name?: string | null;
  type?: string | null;
  name: { text: string; original_text?: string | null };
  options?:
    | readonly { value_id: string; label: { text: string; original_text?: string | null } }[]
    | null;
}

/** The parts of an answer these rules read. */
export interface AreasAnswer {
  value: string | null;
  valueIds: readonly string[];
}

/** The parts of a role list (`listRoleLists`) the picker text needs. */
export interface AreasList {
  label: string;
  levels?: readonly string[] | null;
  options?: readonly { id: string; label: string; addon?: boolean | null }[] | null;
}

/** One tick: an option id and, for an area, View or Design. */
export interface AreasChoice {
  id: string;
  level?: string | null;
}

/** The picker's state, as the access feature's `RoleSelection`. */
export interface AreasSelection {
  choices: AreasChoice[];
  other: string;
}

/** The role list of `listRoleLists` the picker offers. */
export const BLUESTAR_LIST_KEY = "bluestar";
/** The dash between a pick's parts ("Bluestar – MO – View"), as the server writes it. */
const DASH = "–";
/** Between picks: one line in Ivanti, readable in a summary. */
const SEPARATOR = "; ";
/** An add-on has no level; the line says what it is, as the server writes it. */
const ADDON_WORDS = "global role / add-on";

/** The forms' internal names for the two fields (Business Systems Access, User Access Request). */
const SYSTEM_FIELD = "SystemApplication";
const ROLES_FIELD = "RolesNeeded";
/** Their labels, folded, for a form whose internal names drift. */
const SYSTEM_LABEL = "systemorapplication";
const ROLES_LABEL = "rolesoraccessneeded";
/** The system answers that mean Bluestar, folded ("365FO_BS", "Bluestar"). */
const BLUESTAR_SYSTEMS = new Set(["365fobs", "bluestar"]);

/** Lower case, letters and digits only: "365FO_BS" and "365fo bs" are the same. */
export function fold(text: string | null | undefined): string {
  return (text ?? "").toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");
}

/** A label as the tenant wrote it, before any translation. */
function originalText(label: { text: string; original_text?: string | null }): string {
  return label.original_text ?? label.text;
}

function findField<P extends AreasParameter>(
  parameters: readonly P[],
  internal: string,
  label: string,
): P | undefined {
  return (
    parameters.find((parameter) => parameter.internal_name === internal) ??
    parameters.find((parameter) => fold(originalText(parameter.name)) === label)
  );
}

/**
 * The id of the "Roles or access needed" text field to show as the Bluestar
 * area picker, or null: the form has both fields and its system answer is
 * 365FO_BS (or Bluestar).
 */
export function bluestarRolesParameterId(
  parameters: readonly AreasParameter[],
  answers: Readonly<Record<string, AreasAnswer | undefined>>,
): string | null {
  const roles = findField(parameters, ROLES_FIELD, ROLES_LABEL);
  if (roles === undefined || (roles.type !== "TEXT" && roles.type !== "TEXTAREA")) {
    return null;
  }
  const system = findField(parameters, SYSTEM_FIELD, SYSTEM_LABEL);
  const answer = system === undefined ? undefined : answers[system.parameter_id];
  if (system === undefined || answer === undefined) {
    return null;
  }
  const picked =
    answer.valueIds.length > 0
      ? answer.valueIds.map((valueId) => {
          const option = (system.options ?? []).find((each) => each.value_id === valueId);
          return option === undefined ? valueId : originalText(option.label);
        })
      : [answer.value ?? ""];
  return picked.some((label) => BLUESTAR_SYSTEMS.has(fold(label))) ? roles.parameter_id : null;
}

/**
 * The ticks as the text Ivanti gets, in the list's order: "Bluestar – MO –
 * View; Bluestar – Open Id (global role / add-on); Bluestar – Other: …".
 * "Other" is kept as typed (not trimmed) so a space can be typed in it; an
 * id the list does not know is dropped.
 */
export function areasText(list: AreasList, selection: AreasSelection): string {
  const chosen = new Map(selection.choices.map((choice) => [choice.id, choice]));
  const parts: string[] = [];
  for (const option of list.options ?? []) {
    const choice = chosen.get(option.id);
    if (choice === undefined) {
      continue;
    }
    if (option.addon === true) {
      parts.push(`${list.label} ${DASH} ${option.label} (${ADDON_WORDS})`);
    } else {
      const level = choice.level ?? list.levels?.[0] ?? null;
      parts.push(
        level
          ? `${list.label} ${DASH} ${option.label} ${DASH} ${level}`
          : `${list.label} ${DASH} ${option.label}`,
      );
    }
  }
  if (selection.other.trim() !== "") {
    parts.push(`${list.label} ${DASH} Other: ${selection.other}`);
  }
  return parts.join(SEPARATOR);
}

/**
 * The ticks a text names, read back. Each "<list> – <option> [– <level>]"
 * part is a tick (an unknown option is skipped, a level the list does not
 * offer falls back to the first). "<list> – Other: …" is "Other", to the end.
 * Anything else — what was typed before the system was Bluestar, a chat
 * prefill — goes to "Other" as it was, so nothing typed is lost.
 */
export function parseAreasText(list: AreasList, text: string | null | undefined): AreasSelection {
  const written = text ?? "";
  const marker = `${list.label} ${DASH} Other: `;
  const at = written.indexOf(marker);
  const head = at < 0 ? written : written.slice(0, at);
  const byLabel = new Map(
    (list.options ?? []).map((option) => [option.label.toLowerCase(), option]),
  );
  const levels = list.levels ?? [];
  const choices: AreasChoice[] = [];
  const loose: string[] = [];
  for (const segment of head.split(/;|\n/)) {
    const trimmed = segment.trim();
    if (trimmed === "") {
      continue;
    }
    const parts = trimmed.split(/\s+[–-]\s+/);
    const option =
      parts.length >= 2 && parts[0]?.toLowerCase() === list.label.toLowerCase()
        ? byLabel.get((parts[1] ?? "").replace(/\s*\([^()]*\)\s*$/, "").toLowerCase())
        : undefined;
    if (option === undefined) {
      loose.push(trimmed);
      continue;
    }
    if (choices.some((choice) => choice.id === option.id)) {
      continue;
    }
    const level =
      option.addon === true
        ? null
        : (levels.find((each) => each.toLowerCase() === (parts[2] ?? "").trim().toLowerCase()) ??
          levels[0] ??
          null);
    choices.push({ id: option.id, level });
  }
  const typed = at < 0 ? "" : written.slice(at + marker.length);
  const other = [...loose, typed].filter((part) => part !== "").join(SEPARATOR);
  return { choices, other };
}
